import { documentEditorTheme, RichTextEditor } from "@cohesive/ui-components";
import { useState } from "react";

/**
 * The automation's prompt — the markdown the agent receives as the opening
 * message of every run. Same rich markdown surface as a skill's SKILL.md
 * (document theme, 16px body), so what's authored here is a real markdown
 * document rather than an unstyled string.
 *
 * Controlled by the pane's draft: edits raise `onChange`, and nothing is
 * written until Save. The editor is seeded once (the parent keys it per
 * schedule and bumps that key to discard), so the value never churns mid-edit.
 */
export function AutomationInstructionsEditor({
  namespaceKey,
  value,
  onChange,
  readOnly,
}: {
  /** Stable per schedule + reset generation; also the Lexical namespace. */
  namespaceKey: string;
  value: string;
  onChange: (markdown: string) => void;
  readOnly?: boolean;
}) {
  const [seed] = useState(value);

  return (
    // The framed surface is the affordance: this reads as an editable
    // document, not a caption. Focus lights the border rather than adding a
    // ring, matching the app's other framed inputs.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border bg-background focus-within:border-n-40">
      <RichTextEditor
        value={seed}
        onChange={onChange}
        mode="rich"
        showToolbar={false}
        enableSkills={false}
        enableEntities={false}
        enablePlaceholders={false}
        readOnly={readOnly}
        // Render at the same scale as the skill/document editors: doc theme
        // (16px body, em-based headings) instead of the default prose-sm.
        theme={documentEditorTheme}
        applyProseStyles={false}
        placeholder="Write what the agent should do on each run…"
        placeholderClassName="px-3 py-2.5 text-[16px] text-muted-foreground/50"
        namespace={`automation-instructions-${namespaceKey}`}
        className="flex h-full min-h-0 flex-col border-0 bg-transparent"
        editorClassName="h-full min-h-0 max-h-none overflow-y-auto px-3 py-2.5 text-[16px] leading-[1.6] text-foreground"
      />
    </div>
  );
}
